import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useSetProductDiscount } from '../../hooks/useProducts';
import type { Product } from '../../types';
import { Button, Modal, RadioGroup, Radio, Input } from '@shared/ui';

interface SetDiscountModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: number;
    product: Product;
    onSuccess?: () => void;
}

export default function SetDiscountModal({
    isOpen,
    onClose,
    productId,
    product,
    onSuccess,
}: SetDiscountModalProps) {
    const [discountType, setDiscountType] = useState<'discount' | 'price'>('discount');
    const [discountValue, setDiscountValue] = useState('');
    const setDiscountMutation = useSetProductDiscount();

    const formatPrice = (price: number): string => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'RUB',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const hasDiscount = !!(product.discount && product.discountedPrice);

    const handleSetDiscount = async () => {
        if (!productId || !product) return;

        const value = parseFloat(discountValue);
        if (isNaN(value) || value < 0) {
            notifications.show({
                title: 'Ошибка',
                message: 'Введите корректное значение',
                color: 'red',
            });
            return;
        }

        let data: { discount?: number; discountedPrice?: number } = {};

        if (discountType === 'discount') {
            if (value > 100) {
                notifications.show({
                    title: 'Ошибка',
                    message: 'Скидка не может быть больше 100%',
                    color: 'red',
                });
                return;
            }
            data.discount = value;
        } else {
            if (value > product.price) {
                notifications.show({
                    title: 'Ошибка',
                    message: 'Цена со скидкой не может быть больше базовой цены',
                    color: 'red',
                });
                return;
            }
            data.discountedPrice = value;
        }

        try {
            await setDiscountMutation.mutateAsync({
                id: productId,
                data
            });
            handleClose();
            notifications.show({
                title: 'Успешно',
                message: 'Скидка установлена',
                color: 'green',
            });
            onSuccess?.();
        } catch (error) {
            console.error('Ошибка при установке скидки:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при установке скидки',
                color: 'red',
            });
        }
    };

    const handleRemoveDiscount = async () => {
        if (!productId) return;
        try {
            await setDiscountMutation.mutateAsync({
                id: productId,
                data: { discount: 0 }
            });
            handleClose();
            notifications.show({
                title: 'Успешно',
                message: 'Скидка удалена',
                color: 'green',
            });
            onSuccess?.();
        } catch (error) {
            console.error('Ошибка при удалении скидки:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении скидки',
                color: 'red',
            });
        }
    };

    const handleClose = () => {
        setDiscountValue('');
        setDiscountType('discount');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size={400}
            title="Установить скидку"
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <RadioGroup
                    label="Тип:"
                    value={discountType}
                    onChange={(value) => {
                        setDiscountType(value as 'discount' | 'price');
                        setDiscountValue('');
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '0.75rem' }}>
                        <Radio value="discount" label="Скидка (%)" />
                        <Radio value="price" label="Цена со скидкой" />
                    </div>
                </RadioGroup>

                <div>
                    <Input
                        type="number"
                        label={discountType === 'discount' ? 'Скидка (%)' : 'Цена со скидкой (₽)'}
                        min="0"
                        max={discountType === 'discount' ? 100 : undefined}
                        step="0.01"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        placeholder={
                            discountType === 'discount'
                                ? 'Введите процент скидки (0-100)'
                                : `Введите цену со скидкой (макс. ${formatPrice(product.price)})`
                        }
                    />
                    {product && (() => {
                        const value = parseFloat(discountValue);
                        if (!isNaN(value) && value >= 0) {
                            if (discountType === 'discount' && value <= 100) {
                                const discountedPrice = Math.round(product.price * (1 - value / 100));
                                return (
                                    <div style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: '#666' }}>
                                        Цена со скидкой: <strong style={{ color: '#333' }}>{formatPrice(discountedPrice)}</strong>
                                    </div>
                                );
                            } else if (discountType === 'price' && value <= product.price) {
                                const discount = Math.round(((product.price - value) / product.price) * 100);
                                return (
                                    <div style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: '#666' }}>
                                        Скидка: <strong style={{ color: '#333' }}>{discount}%</strong>
                                        <span style={{ marginLeft: '0.375rem', color: '#999' }}>
                                            (Базовая цена: {formatPrice(product.price)})
                                        </span>
                                    </div>
                                );
                            }
                        }
                        return null;
                    })()}
                </div>
                <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <Button
                        onClick={handleClose}
                        variant='secondary'
                    >
                        Отмена
                    </Button>
                    {
                        hasDiscount ? (
                            <>
                                <Button
                                    onClick={handleSetDiscount}
                                    disabled={setDiscountMutation.isPending}
                                    loading={setDiscountMutation.isPending}
                                >
                                    Изменить
                                </Button>
                                <Button
                                    onClick={handleRemoveDiscount}
                                    disabled={setDiscountMutation.isPending}
                                    loading={setDiscountMutation.isPending}
                                    style={{ background: '#dc3545' }}
                                >
                                    Удалить скидку
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={handleSetDiscount}
                                disabled={setDiscountMutation.isPending}
                                loading={setDiscountMutation.isPending}
                            >
                                Установить
                            </Button>
                        )
                    }
                </div>
            </div>
        </Modal>
    );
}

